import React, { useCallback, useEffect, useRef, useState } from 'react';

import { datePadStart } from '../../../../../helpers';
import { useTranslation } from '../../../../../hooks';
import { T } from '../../../../../constants';

interface SidebarTimerProps {
  endDate: string;
  startDate?: string;
}

const initialTimer = { days: 0, hours: 0, minutes: 0, seconds: 0 };

export const SidebarTimer: React.FC<SidebarTimerProps> = ({ endDate, startDate }) => {
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [timer, setTimer] = useState(initialTimer);
  const [label, setLabel] = useState(T.DURATION);
  const t = useTranslation();

  const clearTimerInterval = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
  }, []);

  const updateTimer = useCallback(() => {
    const now = Date.now();
    const end = new Date(endDate).getTime();

    if (end <= now) {
      setTimer({ ...initialTimer });
      clearTimerInterval();
      return;
    }

    let target = end;
    if (startDate) {
      const start = new Date(startDate).getTime();
      setLabel(start <= now ? T.ENDS_IN : T.STARTS_IN);
      target = start <= now ? end : start;
    }

    const duration = target - now;
    setTimer({
      days: Math.floor(duration / (1000 * 60 * 60 * 24)),
      hours: Math.floor((duration / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((duration / 1000 / 60) % 60),
      seconds: Math.floor((duration / 1000) % 60),
    });

    if (duration / 1000 < 1) {
      clearTimerInterval();
    }
  }, [endDate, startDate, clearTimerInterval]);

  useEffect(() => {
    intervalIdRef.current = setInterval(updateTimer, 900);
    return () => clearTimerInterval();
  }, [updateTimer, clearTimerInterval]);

  return (
    <>
      <p className="duration-text">{t(label)}</p>
      <div className="duration-clock">
        <div className="duration-clock-items">
          <span className="duration-clock-items-number">{datePadStart(timer.days)}</span>
          <span className="duration-clock-items-text">{t(T.DAYS_TOURNAMENT)}</span>
        </div>
        <div className="duration-clock-items-colon">:</div>
        <div className="duration-clock-items">
          <span className="duration-clock-items-number">{datePadStart(timer.hours)}</span>
          <span className="duration-clock-items-text">{t(T.HRS_TOURNAMENT)}</span>
        </div>
        <div className="duration-clock-items-colon">:</div>
        <div className="duration-clock-items">
          <span className="duration-clock-items-number">{datePadStart(timer.minutes)}</span>
          <span className="duration-clock-items-text">{t(T.MINS_TOURNAMENT)}</span>
        </div>
        <div className="duration-clock-items-colon">:</div>
        <div className="duration-clock-items">
          <span className="duration-clock-items-number">{datePadStart(timer.seconds)}</span>
          <span className="duration-clock-items-text">{t(T.SECS_TOURNAMENT)}</span>
        </div>
      </div>
    </>
  );
};
